import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { getAppliedPaymentCandidates, getAppliedPaymentCandidatesPaginated } from 'services/reconciliationService';
import { getApiEndpoint, API_PARAMS, LOOKUP_CONFIGS } from '../constants';
import { CustomerData, InvoiceData, PaymentData } from '../types';
import { addLogEntry } from '../utils/devTabLogger';

type LookupType = 'customer' | 'invoice' | 'payment';

interface CrawlFilters {
  ignoreArchivedInvoices: boolean;
  ignoreUnsentInvoices: boolean;
}

interface UseLookupDataProps {
  type: LookupType;
  items?: any[];
  externalSearchId?: string;
  onSearchIdChange?: (id: string) => void;
  crawlFilters?: CrawlFilters;
}

export interface CrawlDataRow {
  timestamp: string;
  id: string;
  qboId?: string;
  matches: number;
  mismatches: number;
  totalChecked: number;
  status: 'synced' | 'mismatch' | 'not_found' | 'error';
  details: string;
  type: 'customer' | 'invoice' | 'payment';
}

interface UseLookupDataReturn {
  loading: boolean;
  qboLoading: boolean;
  searchId: string;
  searchedData: CustomerData | InvoiceData | PaymentData | null;
  qboData: any | null;
  setSearchId: (id: string) => void;
  handleLookup: () => Promise<void>;
  handleBack: () => void;
  handleRefresh: () => Promise<void>;
  handleNext: () => Promise<void>;
  handleLookupRefresh: () => Promise<void>;
  handleCrawl: () => void;
  handleStopCrawl: () => void;
  isCrawling: boolean;
  config: typeof LOOKUP_CONFIGS[LookupType];
  lookupRefreshText: string;
  showBackButton: boolean;
  status: 'loading' | 'error' | 'eserv-only' | 'qbo-only' | 'both' | 'ready';
  statusText: string;
  crawlData: CrawlDataRow[];
  clearCrawlData: () => void;
}

export const useLookupData = ({ type, items = [], externalSearchId, onSearchIdChange, crawlFilters }: UseLookupDataProps): UseLookupDataReturn => {
  const [loading, setLoading] = useState(false);
  const [qboLoading, setQboLoading] = useState(false);
  const [searchId, setSearchId] = useState<string>('');
  const [searchedData, setSearchedData] = useState<CustomerData | InvoiceData | PaymentData | null>(null);
  const [qboData, setQboData] = useState<any | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [cachedItems, setCachedItems] = useState<any[]>([]);
  const [isCrawling, setIsCrawling] = useState(false);
  const [crawlStartIndex, setCrawlStartIndex] = useState<number>(0);
  const [crawlData, setCrawlData] = useState<CrawlDataRow[]>([]);
  const lastCustomerQboIdRef = useRef<string | undefined>(undefined);
  const lastNavigationActionRef = useRef<'next' | 'back' | 'lookup' | null>(null);

  // Transform payment data to include associated invoices
  const transformPaymentData = (payment: any): PaymentData => {
    const associated_invoices = payment.applications 
      ? payment.applications.map((app: any) => app.invoice_id) 
      : [];
    
    return {
      ...payment,
      fk_bcustomer_id: payment.fk_bcustomer_id,
      associated_invoices
    };
  };

  // Sync with external searchId and auto-trigger lookup
  useEffect(() => {
    if (externalSearchId !== undefined && externalSearchId !== searchId && externalSearchId.trim()) {
      console.log(`🔍 External search ID changed to: ${externalSearchId}`);
      setSearchId(externalSearchId);
      
      // Trigger lookup directly with the new ID
      setLoading(true);
      
      // Use the externalSearchId directly to avoid state update delays
      if (type === 'payment') {
        // Fetch payment from API
        const endpoint = `/api/v1/company/quickbooks-online/reconciliation/dev/payments/${externalSearchId}`;
        axios.get(`${process.env.REACT_APP_SERVER_URL}${endpoint}`)
          .then(response => {
            if (response.data?.data) {
              const paymentData = response.data.data;
              setSearchedData({
                payment_id: paymentData.id,
                payment_amount: paymentData.amount,
                received_on: paymentData.received_on,
                payment_qbo_id: paymentData.payment_qbo_id,
                fk_bcustomer_id: paymentData.fk_bcustomer_id,
                associated_invoices: paymentData.applications?.map((app: any) => app.invoice_id) || []
              });
              setLoading(false);
              console.log('🎯 Found payment for auto-lookup:', paymentData);
            } else {
              setSearchedData({ error: 'Payment not found' });
              setLoading(false);
            }
          })
          .catch(error => {
            console.error(`Failed to auto-lookup payment:`, error);
            setSearchedData({ error: error.response?.data?.message || 'Payment not found' });
            setLoading(false);
          });
      } else {
        // API lookup for customer/invoice
        const endpoint = getApiEndpoint(type as 'customer' | 'invoice', 'single', externalSearchId);
        axios.get(`${process.env.REACT_APP_SERVER_URL}${endpoint}`)
          .then(response => {
            const data = response.data?.data || response.data;
            setSearchedData(data);
            setLoading(false);
            console.log(`🎯 Found ${type} for auto-lookup:`, data);
          })
          .catch(error => {
            console.error(`Failed to auto-lookup ${type}:`, error);
            setSearchedData({ error: error.message });
            setLoading(false);
          });
      }
    }
  }, [externalSearchId]);

  // Notify parent of searchId changes
  const handleSetSearchId = (id: string) => {
    setSearchId(id);
    onSearchIdChange?.(id);
  };

  const config = LOOKUP_CONFIGS[type];

  // Auto-load the first item when component mounts (for all types)
  useEffect(() => {
    handleLookup();
  }, [type]); // Only run when type changes

  // Auto-trigger QBO lookup whenever searchedData changes
  useEffect(() => {
    console.log(`🔄 useEffect triggered - searchedData:`, searchedData, 'loading:', loading, 'type:', type);
    if (searchedData && !searchedData.error) {
      // Clear old QBO data when customer changes to ensure fresh fetch
      if (type === 'customer') {
        const customerData = searchedData as CustomerData;
        const currentQboId = customerData.customer_qbo_id;
        // Only clear if the QBO ID has changed (different customer)
        if (lastCustomerQboIdRef.current !== undefined && lastCustomerQboIdRef.current !== currentQboId) {
          console.log(`🔄 Customer changed - clearing old QBO data (old: ${lastCustomerQboIdRef.current}, new: ${currentQboId})`);
          setQboData(null);
        }
        // Update ref to track current customer
        lastCustomerQboIdRef.current = currentQboId;
      }
      console.log(`🔄 Auto-triggering QBO lookup for ${type} due to data change`);
      handleQboLookup();
    } else {
      console.log(`🔄 Skipping QBO lookup - searchedData:`, !!searchedData, 'error:', searchedData?.error);
      // Clear QBO data if searchedData is invalid
      if (searchedData?.error) {
        setQboData(null);
      }
      // Reset ref on error
      if (type === 'customer') {
        lastCustomerQboIdRef.current = undefined;
      }
    }
  }, [searchedData, type]); // Run when searchedData or type changes

  const handleLookup = async (): Promise<void> => {
    try {
      setLoading(true);
      // Mark this as a 'lookup' action for logging
      lastNavigationActionRef.current = 'lookup';
      
      if (type === 'payment') {
        await handlePaymentLookup();
      } else {
        await handleApiLookup();
        // QBO lookup will be triggered automatically by useEffect
      }
    } catch (error) {
      console.error(`Failed to lookup ${type}:`, error);
      setSearchedData({ error: error.message });
      lastNavigationActionRef.current = null;
    } finally {
      setLoading(false);
    }
  };

  // Next button - always goes to next item, loops back
  const handleNext = async (): Promise<void> => {
    console.log(`🔄 Next button clicked for ${type}`);
    // Mark this as a 'next' action for logging
    lastNavigationActionRef.current = 'next';
    try {
      setLoading(true);
      
      if (type === 'payment') {
        await handlePaymentNext();
      } else {
        await handleApiNext();
      }
    } catch (error) {
      console.error(`Failed to get next ${type}:`, error);
      setSearchedData({ error: error.message });
      lastNavigationActionRef.current = null;
    } finally {
      setLoading(false);
    }
  };

  // Next for API-based types (customer, invoice)
  const handleApiNext = async (): Promise<void> => {
    console.log(`🔄 handleApiNext called - cachedItems.length:`, cachedItems.length, 'currentIndex:', currentIndex);
    if (cachedItems.length > 0 && currentIndex < cachedItems.length) {
      // Use cached items for navigation (still within current batch)
      const item = cachedItems[currentIndex];
      console.log(`🔄 Next item selected from cache:`, item);
      
      // For customers, fetch full data including associated invoices/payments
      if (type === 'customer' && item.id) {
        try {
          const endpoint = getApiEndpoint(type as 'customer', 'single', item.id.toString());
          const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}${endpoint}`);
          const fullData = response.data?.data || response.data;
          console.log(`🔄 Fetched full customer data with associated data:`, fullData);
          setSearchedData(fullData);
        } catch (error) {
          console.error('Failed to fetch full customer data:', error);
          setSearchedData(item); // Fallback to cached item
        }
      } else {
        setSearchedData(item);
      }
      
      setSearchId(''); // Clear input
      setCurrentIndex(currentIndex + 1);
      
      // QBO lookup will be triggered automatically by useEffect
      console.log(`Next ${type} (index ${currentIndex}):`, item);
    } else {
      // Reached end of cached items or no cache - fetch next batch from database
      // Get the minimum ID from the current batch to continue pagination
      const minId = cachedItems.length > 0 
        ? Math.min(...cachedItems.map(item => item.id || 0))
        : (searchedData && (searchedData as any).id ? (searchedData as any).id : null);
      
      console.log(`🔄 Fetching next batch with afterId: ${minId}`);
      
      const endpoint = getApiEndpoint(type as 'customer' | 'invoice', 'list', undefined);
      const params: any = { page: 1, pageSize: 10, search: '' };
      if (minId) {
        params.afterId = minId;
      }
      
      const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}${endpoint}`, { params });

      const dataKey = config.dataKey;
      if (response.data?.[dataKey] && response.data[dataKey].length > 0) {
        const items = response.data[dataKey];
        setCachedItems(items);
        const item = items[0];
        
        // For customers, fetch full data including associated invoices/payments
        if (type === 'customer' && item.id) {
          try {
            const endpoint = getApiEndpoint(type as 'customer', 'single', item.id.toString());
            const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}${endpoint}`);
            const fullData = response.data?.data || response.data;
            console.log(`🔄 Fetched full customer data with associated data:`, fullData);
            setSearchedData(fullData);
          } catch (error) {
            console.error('Failed to fetch full customer data:', error);
            setSearchedData(item); // Fallback to cached item
          }
        } else {
          setSearchedData(item);
        }
        
        setSearchId('');
        setCurrentIndex(1);
        
        // QBO lookup will be triggered automatically by useEffect
        console.log(`Next ${type} (fresh batch${minId ? `, afterId: ${minId}` : ''}):`, item);
      } else {
        // No more items found - we've reached the end
        console.log(`🔄 No more ${type}s found - reached end of database`);
        setSearchedData({ error: `No more ${type}s found` });
      }
    }
  };

  // Next for payment type (next = decrement = lower IDs)
  const handlePaymentNext = async (): Promise<void> => {
    if (cachedItems.length > 0 && currentIndex < cachedItems.length) {
      // Use cached items for navigation (still within current batch)
      const item = cachedItems[currentIndex];
      setSearchedData(transformPaymentData(item));
      setSearchId(''); // Clear input
      setCurrentIndex(currentIndex + 1);
      
      console.log(`Next payment (index ${currentIndex}):`, item);
    } else {
      // Reached end of cached items - fetch next batch using cursor-based pagination
      // "Next" = decrement = lower IDs, so use lastCursor (lowest ID in current batch)
      const lastPaymentId = cachedItems.length > 0 
        ? cachedItems[cachedItems.length - 1]?.payment_id || cachedItems[cachedItems.length - 1]?.id
        : (searchedData && (searchedData as PaymentData).payment_id ? (searchedData as PaymentData).payment_id.toString() : null);
      
      console.log(`🔄 Fetching next payment batch (decrement)${lastPaymentId ? ` (cursor: ${lastPaymentId})` : ''}`);
      
      try {
        const response = await getAppliedPaymentCandidatesPaginated(10, lastPaymentId, 'next', '');
        if (response?.items && response.items.length > 0) {
          setCachedItems(response.items);
          const item = response.items[0];
          setSearchedData(transformPaymentData(item));
          setSearchId('');
          setCurrentIndex(1);
          
          // Check if there are more items - if not, we've reached the lowest ID
          if (response.hasMore === false) {
            console.log(`🔄 Reached lowest payment ID - no more payments available`);
            // Don't set error yet, let the crawl continue to process this last batch
            // The error will be set on the next call when items.length === 0
          }
          
          console.log(`Next payment (fresh batch, decrement):`, item);
        } else {
          console.log(`🔄 No more payments found - reached end`);
          setSearchedData({ error: 'No more payments found' });
        }
      } catch (error) {
        console.error('Failed to fetch next payment batch:', error);
        setSearchedData({ error: 'Failed to fetch payments' });
      }
    }
  };

  // Lookup/Refresh button - lookup if input, refresh current if no input
  const handleLookupRefresh = async (): Promise<void> => {
    console.log(`🔄 Refresh button clicked for ${type} - hasInputValue:`, hasInputValue, 'hasCurrentData:', hasCurrentData);
    if (hasInputValue) {
      // Input present - do lookup
      console.log(`🔄 Doing lookup for input value`);
      lastNavigationActionRef.current = 'lookup';
      await handleLookup();
    } else if (hasCurrentData) {
      // No input but have current data - refresh current item
      console.log(`🔄 Refreshing current ${type} data`);
      if (type === 'payment') {
        await handlePaymentRefresh();
      } else {
        await handleApiRefresh();
      }
    } else {
      // No input and no current data - go to max ID
      console.log(`🔄 Going to max ${type} ID`);
      await handleRefresh();
    }
  };

  // Refresh for API-based types (customer, invoice)
  const handleApiRefresh = async (): Promise<void> => {
    try {
      setLoading(true);
      // Clear QBO data to force fresh lookup
      setQboData(null);
      
      let currentId: string | undefined;
      if (type === 'customer') {
        currentId = (searchedData as CustomerData).id?.toString();
      } else if (type === 'invoice') {
        currentId = (searchedData as InvoiceData).id?.toString();
      }
      
      if (currentId) {
        // Fetch fresh EServ data
        const endpoint = getApiEndpoint(type as 'customer' | 'invoice', 'single', currentId);
        const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}${endpoint}`);
        
        const freshData = response.data?.data || response.data;
        setSearchedData(freshData);
        
        // QBO lookup will be triggered automatically by useEffect
        console.log(`Refreshed current ${type} data:`, freshData);
      }
    } catch (error) {
      console.error(`Failed to refresh current ${type}:`, error);
      setSearchedData({ error: error.message });
    } finally {
      setLoading(false);
    }
  };

  // Refresh for payment type
  const handlePaymentRefresh = async (): Promise<void> => {
    try {
      setLoading(true);
      // Clear QBO data to force fresh lookup
      setQboData(null);
      
      const currentId = (searchedData as PaymentData).payment_id?.toString();
      if (currentId) {
        // Fetch fresh payment data from API
        const endpoint = `/api/v1/company/quickbooks-online/reconciliation/dev/payments/${currentId}`;
        const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}${endpoint}`);
        
        if (response.data?.data) {
          const paymentData = response.data.data;
          setSearchedData({
            payment_id: paymentData.id,
            payment_amount: paymentData.amount,
            received_on: paymentData.received_on,
            payment_qbo_id: paymentData.payment_qbo_id,
            fk_bcustomer_id: paymentData.fk_bcustomer_id,
            associated_invoices: paymentData.applications?.map((app: any) => app.invoice_id) || []
          });
          console.log(`Refreshed current payment data:`, paymentData);
        } else {
          setSearchedData({ error: 'Payment not found' });
        }
      }
    } catch (error) {
      console.error(`Failed to refresh current payment:`, error);
      setSearchedData({ error: error.response?.data?.message || error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleQboLookup = async (): Promise<void> => {
    console.log(`🚀 handleQboLookup called - searchedData:`, searchedData, 'error:', searchedData?.error);
    if (!searchedData || searchedData.error) {
      console.log(`❌ handleQboLookup early return - no data or error`);
      return;
    }
    
    setQboLoading(true);
    try {
      // Get QBO ID from the current data
      let qboId: string | undefined;
      
      if (type === 'customer') {
        qboId = (searchedData as CustomerData).customer_qbo_id;
      } else if (type === 'invoice') {
        qboId = (searchedData as InvoiceData).invoice_qbo_id;
      } else if (type === 'payment') {
        qboId = (searchedData as PaymentData).payment_qbo_id;
      }
      
      if (qboId) {
        // Actually check if the customer exists in QBO
        console.log(`🔍 QBO lookup for ${type} ID: ${qboId} (type: ${typeof qboId})`);
        
        try {
          // Make API call to check if item exists in QBO
          // Use checkAll=true on refresh to check all invoices/payments
          const isRefresh = qboData !== null; // If we already have data, this is a refresh
          
          let endpoint: string;
          if (type === 'customer') {
            endpoint = `/api/v1/company/quickbooks-online/reconciliation/dev/qbo-customer/${qboId}`;
            // Always use checkAll=true for customers to get complete invoice/payment sync status
            // This ensures all invoices are checked, not just the first 20
            endpoint += '?checkAll=true';
          } else if (type === 'invoice') {
            endpoint = `/api/v1/company/quickbooks-online/reconciliation/dev/qbo-invoice/${qboId}`;
          } else if (type === 'payment') {
            endpoint = `/api/v1/company/quickbooks-online/reconciliation/dev/qbo-payment/${qboId}`;
          } else {
            // Unsupported type
            setQboData(null);
            console.log(`❌ QBO lookup not supported for ${type}`);
            return;
          }
          
          console.log(`🌐 Making QBO API call to: ${endpoint}${type === 'customer' ? ' (checkAll=true)' : isRefresh ? ' (full check)' : ' (limited check)'}`);
          const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}${endpoint}`);
          console.log(`📡 QBO API response:`, response.data);
          
          if (response.data && response.data.success) {
            setQboData({ 
              id: qboId, 
              found: true, 
              data: response.data.data,
              warnings: response.data.warnings 
            });
            console.log(`✅ QBO ${type} found:`, response.data.data);
            if (response.data.warnings) {
              console.log(`⚠️ QBO ${type} warnings:`, response.data.warnings);
            }
            
            // For customers, also update the searchedData with sync status (only if not already present to prevent loops)
            if (type === 'customer' && response.data.invoices_sync_status && response.data.payments_sync_status) {
              const currentData = searchedData as CustomerData;
              if (!currentData.invoices_sync_status || !currentData.payments_sync_status) {
                console.log(`📝 Updating customer with sync status data`);
                if (response.data.payment_application_issues) {
                  console.log(`⚠️ Payment application issues detected:`, response.data.payment_application_issues);
                }
                setSearchedData(prev => {
                  const prevData = prev as CustomerData;
                  const updatedData = {
                    ...prev,
                    // Preserve associated invoices and payments from original data
                    associated_invoices: prevData?.associated_invoices || [],
                    associated_payments: prevData?.associated_payments || [],
                    // Update sync status information
                    invoices_sync_status: response.data.invoices_sync_status,
                    invoices_archive_status: response.data.invoices_archive_status || {},
                    invoices_sent_status: response.data.invoices_sent_status || {},
                    payments_sync_status: response.data.payments_sync_status,
                    payment_application_issues: response.data.payment_application_issues
                  };
                  
                  // Calculate match/mismatch data
                  const invoicesSyncStatus = response.data.invoices_sync_status || {};
                  const paymentsSyncStatus = response.data.payments_sync_status || {};
                  const totalInvoices = Object.keys(invoicesSyncStatus).length;
                  const syncedInvoices = Object.values(invoicesSyncStatus).filter((s: any) => s === true).length;
                  const unsyncedInvoices = Object.values(invoicesSyncStatus).filter((s: any) => s === false).length;
                  const totalPayments = Object.keys(paymentsSyncStatus).length;
                  const syncedPayments = Object.values(paymentsSyncStatus).filter((s: any) => s === true).length;
                  const unsyncedPayments = Object.values(paymentsSyncStatus).filter((s: any) => s === false).length;
                  
                  const customerMatch = 1; // Customer found in QBO
                  const totalMatches = customerMatch + syncedInvoices + syncedPayments;
                  const totalMismatches = unsyncedInvoices + unsyncedPayments;
                  const totalChecked = 1 + totalInvoices + totalPayments;
                  
                  const details = [
                    `Customer: 1 match, 0 mismatch`,
                    `Invoices: ${syncedInvoices}/${totalInvoices} matches, ${unsyncedInvoices}/${totalInvoices} mismatches`,
                    `Payments: ${syncedPayments}/${totalPayments} matches, ${unsyncedPayments}/${totalPayments} mismatches`
                  ].join('\n');
                  
                  // Determine action message based on last navigation
                  const actionType = lastNavigationActionRef.current;
                  let actionMessage = `Initial findings for customer lookup`;
                  if (actionType === 'next') {
                    actionMessage = `Next: Customer navigation results`;
                  } else if (actionType === 'back') {
                    actionMessage = `Back: Customer navigation results`;
                  }
                  
                  addLogEntry({
                    action: 'lookup',
                    type: 'customer',
                    id: String((prev as any)?.id || 'unknown'),
                    success: true,
                    qboId: qboId || undefined,
                    matches: totalMatches,
                    mismatches: totalMismatches,
                    totalChecked: totalChecked,
                    message: actionMessage,
                    details: details
                  });
                  
                  // Clear navigation action after logging
                  lastNavigationActionRef.current = null;
                  
                  return updatedData;
                });
              } else {
                console.log(`✅ Sync status already present, skipping update to prevent loop`);
              }
            } else if (type === 'customer') {
              // Log initial findings even if sync status not yet available
              const actionType = lastNavigationActionRef.current;
              let actionMessage = `Customer found in QBO (sync status pending)`;
              if (actionType === 'next') {
                actionMessage = `Next: Customer found in QBO (sync status pending)`;
              } else if (actionType === 'back') {
                actionMessage = `Back: Customer found in QBO (sync status pending)`;
              }
              
              addLogEntry({
                action: 'lookup',
                type: 'customer',
                id: String((searchedData as any)?.id || 'unknown'),
                success: true,
                qboId: qboId || undefined,
                matches: 1,
                mismatches: 0,
                totalChecked: 1,
                message: actionMessage
              });
              
              lastNavigationActionRef.current = null;
            }
            
            // For invoices, also update the searchedData with customer sync status
            if (type === 'invoice' && response.data.customer_sync_status) {
              const currentData = searchedData as InvoiceData;
              if (!currentData.customer_sync_status) {
                console.log(`📝 Updating invoice with customer sync status data`);
                setSearchedData(prev => {
                  const invoiceId = (prev as any)?.id || 'unknown';
                  const customerQboId = (prev as InvoiceData)?.customer_qbo_id;
                  const customerSynced = response.data.customer_sync_status?.[customerQboId || ''] === true;
                  
                  const invoiceMatch = 1; // Invoice found in QBO
                  const customerMatch = customerQboId ? (customerSynced ? 1 : 0) : 0;
                  const totalMatches = invoiceMatch + customerMatch;
                  const totalMismatches = customerQboId ? (customerSynced ? 0 : 1) : 0;
                  const totalChecked = customerQboId ? 2 : 1;
                  
                  const details = [
                    `Invoice: 1 match, 0 mismatch`,
                    customerQboId ? `Customer: ${customerMatch} match, ${totalMismatches} mismatch` : 'Customer: No QBO ID'
                  ].join('\n');
                  
                  // Determine action message based on last navigation
                  const actionType = lastNavigationActionRef.current;
                  let actionMessage = `Initial findings for invoice lookup`;
                  if (actionType === 'next') {
                    actionMessage = `Next: Invoice navigation results`;
                  } else if (actionType === 'back') {
                    actionMessage = `Back: Invoice navigation results`;
                  }
                  
                  addLogEntry({
                    action: 'lookup',
                    type: 'invoice',
                    id: String(invoiceId),
                    success: true,
                    qboId: qboId || undefined,
                    matches: totalMatches,
                    mismatches: totalMismatches,
                    totalChecked: totalChecked,
                    message: actionMessage,
                    details: details
                  });
                  
                  // Clear navigation action after logging
                  lastNavigationActionRef.current = null;
                  
                  return {
                    ...prev,
                    customer_sync_status: response.data.customer_sync_status
                  };
                });
              } else {
                console.log(`✅ Customer sync status already present, skipping update to prevent loop`);
              }
            } else if (type === 'invoice') {
              // Log initial findings even if customer sync status not available
              const actionType = lastNavigationActionRef.current;
              let actionMessage = `Invoice found in QBO (customer sync status pending)`;
              if (actionType === 'next') {
                actionMessage = `Next: Invoice found in QBO (customer sync status pending)`;
              } else if (actionType === 'back') {
                actionMessage = `Back: Invoice found in QBO (customer sync status pending)`;
              }
              
              addLogEntry({
                action: 'lookup',
                type: 'invoice',
                id: String((searchedData as any)?.id || 'unknown'),
                success: true,
                qboId: qboId || undefined,
                matches: 1,
                mismatches: 0,
                totalChecked: 1,
                message: actionMessage
              });
              
              lastNavigationActionRef.current = null;
            } else if (type === 'payment') {
              // Log initial findings for payment
              const actionType = lastNavigationActionRef.current;
              let actionMessage = `Initial findings for payment lookup`;
              if (actionType === 'next') {
                actionMessage = `Next: Payment navigation results`;
              } else if (actionType === 'back') {
                actionMessage = `Back: Payment navigation results`;
              }
              
              addLogEntry({
                action: 'lookup',
                type: 'payment',
                id: String((searchedData as PaymentData)?.payment_id || 'unknown'),
                success: true,
                qboId: qboId || undefined,
                matches: 1,
                mismatches: 0,
                totalChecked: 1,
                message: actionMessage,
                details: `Payment: 1 match, 0 mismatch`
              });
              
              lastNavigationActionRef.current = null;
            }
          } else {
            // QBO API returned success: false (customer not found)
            setQboData({ 
              id: qboId, 
              found: false,
              error: response.data?.message || 'Not found in QBO'
            });
            console.log(`❌ QBO ${type} not found for ID: ${qboId}, response:`, response.data);
            
            // Clear navigation action ref on error
            lastNavigationActionRef.current = null;
            
            // Log not found
            addLogEntry({
              action: 'lookup',
              type: type as 'customer' | 'invoice' | 'payment',
              id: String((searchedData as any)?.id || (searchedData as any)?.payment_id || 'unknown'),
              success: false,
              qboId: qboId || undefined,
              matches: 0,
              mismatches: 1,
              totalChecked: 1,
              message: `Not found in QBO`,
              error: response.data?.message || 'Not found in QBO'
            });
          }
        } catch (qboError: any) {
          // Clear navigation action ref on error
          lastNavigationActionRef.current = null;
          
          // Handle different types of errors
          const errorStatus = qboError?.response?.status;
          const errorData = qboError?.response?.data;
          
          if (errorStatus === 429) {
            // Rate limit error - set error state so UI can display it
            const errorMsg = errorData?.message || 'QBO API rate limit exceeded. Please wait and try again.';
            setQboData({ 
              id: qboId, 
              found: false,
              error: errorMsg,
              rateLimit: true
            });
            console.log(`⏱️ QBO lookup rate limited for ${type} ID ${qboId}`);
            
            // Log rate limit error
            addLogEntry({
              action: 'lookup',
              type: type as 'customer' | 'invoice' | 'payment',
              id: String((searchedData as any)?.id || (searchedData as any)?.payment_id || 'unknown'),
              success: false,
              qboId: qboId || undefined,
              matches: 0,
              mismatches: 1,
              totalChecked: 1,
              message: `QBO lookup rate limited`,
              error: errorMsg
            });
          } else if (errorStatus === 400 || errorStatus === 404) {
            // Not found or bad request - customer doesn't exist in QBO
            const errorMsg = errorData?.message || `Not found in QBO`;
            setQboData({ 
              id: qboId, 
              found: false,
              error: errorMsg
            });
            console.log(`❌ QBO ${type} not found for ID: ${qboId} (${errorStatus})`);
            
            // Log not found (already logged above in else block, but this handles error responses)
            addLogEntry({
              action: 'lookup',
              type: type as 'customer' | 'invoice' | 'payment',
              id: String((searchedData as any)?.id || (searchedData as any)?.payment_id || 'unknown'),
              success: false,
              qboId: qboId || undefined,
              matches: 0,
              mismatches: 1,
              totalChecked: 1,
              message: `Not found in QBO`,
              error: errorMsg
            });
          } else {
            // Other errors - network, server error, etc.
            const errorMsg = errorData?.message || qboError?.message || 'QBO lookup failed';
            setQboData({ 
              id: qboId, 
              found: false,
              error: errorMsg
            });
            console.log(`❌ QBO lookup failed for ${type} ID ${qboId}:`, qboError.message);
            console.log(`❌ Full error:`, qboError);
            
            // Log lookup error
            addLogEntry({
              action: 'lookup',
              type: type as 'customer' | 'invoice' | 'payment',
              id: String((searchedData as any)?.id || (searchedData as any)?.payment_id || 'unknown'),
              success: false,
              qboId: qboId || undefined,
              matches: 0,
              mismatches: 1,
              totalChecked: 1,
              message: `QBO lookup failed`,
              error: errorMsg
            });
          }
        }
      } else {
        setQboData(null);
        console.log(`❌ No QBO ID found for ${type}, searchedData:`, searchedData);
        
        // Log when no QBO ID is available
        addLogEntry({
          action: 'lookup',
          type: type as 'customer' | 'invoice' | 'payment',
          id: String((searchedData as any)?.id || (searchedData as any)?.payment_id || 'unknown'),
          success: false,
          matches: 0,
          mismatches: 1,
          totalChecked: 1,
          message: `No QBO ID found`,
          error: `Item does not have a QBO ID`
        });
      }
    } catch (error) {
      console.error(`QBO lookup failed for ${type}:`, error);
      setQboData(null);
    } finally {
      setQboLoading(false);
    }
  };

  const handlePaymentLookup = async (): Promise<void> => {
    if (searchId.trim()) {
      // Fetch specific payment from API
      try {
        const endpoint = `/api/v1/company/quickbooks-online/reconciliation/dev/payments/${searchId.trim()}`;
        const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}${endpoint}`);
        
        if (response.data?.data) {
          const paymentData = response.data.data;
          // Transform to match PaymentData interface
          setSearchedData({
            payment_id: paymentData.id,
            payment_amount: paymentData.amount,
            received_on: paymentData.received_on,
            payment_qbo_id: paymentData.payment_qbo_id,
            fk_bcustomer_id: paymentData.fk_bcustomer_id,
            associated_invoices: paymentData.applications?.map((app: any) => app.invoice_id) || []
          });
          console.log('Found payment from API:', paymentData);
        } else {
          setSearchedData({ error: 'Payment not found' });
        }
      } catch (error) {
        console.error('Failed to fetch payment:', error);
        setSearchedData({ error: error.response?.data?.message || 'Payment not found' });
      }
    } else {
      // Get first payment by ID descending using reconciliation API
      try {
        const response = await getAppliedPaymentCandidatesPaginated(1, null, 'next', '');
        if (response?.items && response.items.length > 0) {
          const firstPayment = response.items[0]; // First item is highest ID (descending order)
          setSearchedData(transformPaymentData(firstPayment));
          setCachedItems(response.items);
          setCurrentIndex(1);
          console.log('First payment (highest ID):', firstPayment);
        } else {
          setSearchedData({ error: 'No payments found' });
        }
      } catch (error: any) {
        console.error('Failed to refresh payment:', error);
        const errorMessage = error.response?.status === 401 
          ? 'Authentication failed. Please refresh the page.'
          : error.response?.data?.message || error.message || 'Failed to refresh payment';
        setSearchedData({ error: errorMessage });
        // Don't clear cached items on error - keep Back button available
        if (cachedItems.length === 0 && searchedData && !searchedData.error) {
          // Only clear if we have no cached items and no current data
        }
      }
    }
  };

  const handleApiLookup = async (): Promise<void> => {
    if (searchId.trim()) {
      // For single lookups by ID
      const endpoint = getApiEndpoint(type as 'customer' | 'invoice', 'single', searchId.trim());
      const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}${endpoint}`);
      
      const data = response.data?.data || response.data;
      
      // For customers, ensure we have full data with associated invoices/payments
      // If the initial response doesn't have associated_invoices, it means they weren't loaded
      // The QBO lookup will add sync status, but we need the invoice IDs from EServ first
      if (type === 'customer' && data && !data.error) {
        console.log(`🔍 Customer lookup result - has associated_invoices:`, !!data.associated_invoices, 'count:', data.associated_invoices?.length || 0);
        if (!data.associated_invoices || data.associated_invoices.length === 0) {
          console.log(`⚠️ Customer data missing associated_invoices - this should not happen`);
        }
      }
      
      setSearchedData(data);
      setSearchId(''); // Clear input after successful lookup
      setCurrentIndex(0); // Reset index
      setCachedItems([]); // Clear cache
      console.log(`Found ${type}:`, data);
      // QBO lookup will be triggered automatically by useEffect when searchedData changes
    } else {
      // For list lookups or "Next" functionality
      if (cachedItems.length > 0 && currentIndex < cachedItems.length) {
        // Use cached items for "Next" functionality
        const item = cachedItems[currentIndex];
        
        // For customers from list, fetch full data including associated invoices/payments
        if (type === 'customer' && item.id) {
          try {
            const endpoint = getApiEndpoint(type as 'customer', 'single', item.id.toString());
            const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}${endpoint}`);
            const fullData = response.data?.data || response.data;
            console.log(`🔍 List -> Fetching full customer data:`, fullData);
            setSearchedData(fullData);
          } catch (error) {
            console.error('Failed to fetch full customer data from list:', error);
            setSearchedData(item); // Fallback to cached item
          }
        } else {
          setSearchedData(type === 'payment' ? transformPaymentData(item) : item);
        }
        
        setSearchId(''); // Clear input
        setCurrentIndex(prev => prev + 1);
        
        // QBO lookup will be triggered automatically by useEffect
        console.log(`Next ${type} (index ${currentIndex}):`, item);
      } else {
        // Fetch new batch of items
        const endpoint = getApiEndpoint(type as 'customer' | 'invoice', 'list', undefined);
        const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}${endpoint}`, {
          params: { page: 1, pageSize: 10, search: '' } // Fetch 10 items for cycling
        });

        const dataKey = config.dataKey;
        console.log(`🔍 ${type} list response:`, response.data);
        console.log(`🔍 Looking for dataKey: ${dataKey} in response`);
        
        if (response.data?.[dataKey] && response.data[dataKey].length > 0) {
          const items = response.data[dataKey];
          setCachedItems(items);
          const item = items[0];
          
          // For customers from list, fetch full data including associated invoices/payments
          if (type === 'customer' && item.id) {
            try {
              const endpoint = getApiEndpoint(type as 'customer', 'single', item.id.toString());
              const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}${endpoint}`);
              const fullData = response.data?.data || response.data;
              console.log(`🔍 Initial load -> Fetching full customer data:`, fullData);
              setSearchedData(fullData);
            } catch (error) {
              console.error('Failed to fetch full customer data on initial load:', error);
              setSearchedData(item); // Fallback to cached item
            }
          } else {
            setSearchedData(item);
          }
          
          setSearchId(''); // Clear input
          setCurrentIndex(1); // Start at index 1 for next click
          
          // QBO lookup will be triggered automatically by useEffect
          console.log(`✅ First ${type} (cached ${items.length} items):`, item);
        } else {
          console.log(`❌ No ${type}s found in response`);
          setSearchedData({ error: `No ${type}s found` });
        }
      }
    }
  };

  // Handle back navigation
  const handleBack = async (): Promise<void> => {
    // Mark this as a 'back' action for logging
    lastNavigationActionRef.current = 'back';
    
    if (cachedItems.length > 0 && currentIndex > 1) {
      // We have cached items and can go back within current batch
      const prevIndex = currentIndex - 2; // Go back to previous item
      const item = cachedItems[prevIndex];
      
      // For customers, fetch full data including associated invoices/payments
      if (type === 'customer' && item.id) {
        try {
          const endpoint = getApiEndpoint(type as 'customer', 'single', item.id.toString());
          const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}${endpoint}`);
          const fullData = response.data?.data || response.data;
          console.log(`🔄 Back - Fetched full customer data with associated data:`, fullData);
          setSearchedData(fullData);
        } catch (error) {
          console.error('Failed to fetch full customer data on back:', error);
          setSearchedData(item); // Fallback to cached item
        }
      } else {
        setSearchedData(item);
      }
      
      setSearchId(''); // Clear input
      setCurrentIndex(prevIndex + 1);
      
      // QBO lookup will be triggered automatically by useEffect
      console.log(`Back to ${type} (index ${prevIndex}):`, item);
    } else {
      // Reached beginning of cached items or no cache - fetch previous batch from database
      // For going backwards, we need items with higher IDs than the current maximum
      // Since backend uses `id < afterId`, we need to use the maximum ID from current batch
      const maxId = cachedItems.length > 0 
        ? Math.max(...cachedItems.map(item => item.id || 0))
        : (searchedData && (searchedData as any).id ? (searchedData as any).id : null);
      
      console.log(`🔄 Fetching previous batch with afterId: ${maxId} (going backwards)`);
      
      try {
        setLoading(true);
        
        if (type === 'payment') {
          // "Back" = increment = higher IDs, so use firstCursor (highest ID in current batch)
          const firstPaymentId = cachedItems.length > 0 && currentIndex <= 1
            ? cachedItems[0]?.payment_id || cachedItems[0]?.id
            : (searchedData && (searchedData as PaymentData).payment_id ? (searchedData as PaymentData).payment_id.toString() : null);
          
          console.log(`🔄 Fetching previous payment batch (increment)${firstPaymentId ? ` (cursor: ${firstPaymentId})` : ''}`);
          
          try {
            const response = await getAppliedPaymentCandidatesPaginated(10, firstPaymentId, 'prev', '');
            if (response?.items && response.items.length > 0) {
              setCachedItems(response.items);
              const item = response.items[response.items.length - 1]; // Last item (highest ID)
              setSearchedData(transformPaymentData(item));
              setSearchId('');
              setCurrentIndex(response.items.length);
              console.log(`Back to payment (increment):`, item);
            } else {
              // No more items with higher IDs, go to first page (highest IDs)
              const firstPageResponse = await getAppliedPaymentCandidatesPaginated(10, null, 'next', '');
              if (firstPageResponse?.items && firstPageResponse.items.length > 0) {
                setCachedItems(firstPageResponse.items);
                const item = firstPageResponse.items[0];
                setSearchedData(transformPaymentData(item));
                setSearchId('');
                setCurrentIndex(1);
                console.log(`Back to first payment:`, item);
              } else {
                setSearchedData({ error: 'No payments found' });
              }
            }
          } catch (error) {
            console.error('Failed to fetch previous payment batch:', error);
            setSearchedData({ error: 'Failed to fetch payments' });
          }
        } else {
          const endpoint = getApiEndpoint(type as 'customer' | 'invoice', 'list', undefined);
          const params: any = { page: 1, pageSize: 10, search: '' };
          // Use maxId to get items with higher IDs (going backwards)
          // Note: Backend uses `id < afterId`, so using maxId will get items with IDs less than maxId
          // For true backwards navigation, we'd need backend support for `id > afterId`
          // For now, we'll fetch items with higher IDs by not using afterId when going backwards
          // This will get us back to the highest IDs
          if (maxId && currentIndex <= 1) {
            // Only use afterId if we're truly at the beginning and want to continue backwards
            // Actually, for backwards, we want higher IDs, so we should NOT use afterId
            // Instead, we'll fetch from the start (highest IDs) and work our way down
            // This is a limitation - true backwards pagination would need backend support
            console.log(`🔄 Note: Backwards pagination limited - fetching from highest IDs`);
          }
          
          const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}${endpoint}`, { params });

          const dataKey = config.dataKey;
          if (response.data?.[dataKey] && response.data[dataKey].length > 0) {
            const items = response.data[dataKey];
            setCachedItems(items);
            const item = items[0];
            
            // For customers, fetch full data including associated invoices/payments
            if (type === 'customer' && item.id) {
              try {
                const endpoint = getApiEndpoint(type as 'customer', 'single', item.id.toString());
                const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}${endpoint}`);
                const fullData = response.data?.data || response.data;
                console.log(`🔄 Back to max - Fetched full customer data with associated data:`, fullData);
                setSearchedData(fullData);
              } catch (error) {
                console.error('Failed to fetch full customer data on back to max:', error);
                setSearchedData(item); // Fallback to cached item
              }
            } else {
              setSearchedData(item);
            }
            
            setSearchId('');
            setCurrentIndex(1);
            
            // QBO lookup will be triggered automatically by useEffect
            console.log(`Back to max ${type}:`, item);
          } else {
            setSearchedData({ error: `No ${type}s found` });
          }
        }
      } catch (error) {
        console.error(`Failed to go back to max ${type}:`, error);
        setSearchedData({ error: error.message });
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle refresh - get fresh data for current customer or search for input ID
  const handleRefresh = async (): Promise<void> => {
    try {
      setLoading(true);
      
      // If there's a value in the input, search for that ID instead
      if (searchId.trim()) {
        console.log(`Refresh searching for ${type} ID: ${searchId.trim()}`);
        
        // Use the regular lookup logic for the input ID
        const endpoint = getApiEndpoint(type as 'customer' | 'invoice', 'single', searchId.trim());
        const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}${endpoint}`);
        
        const data = response.data?.data || response.data;
        setSearchedData(data);
        setSearchId(''); // Clear input after search
        
        // QBO lookup will be triggered automatically by useEffect
        console.log(`Refresh found ${type}:`, data);
      } else {
        // No input value, go to max ID (first in descending order)
        console.log(`Refresh going to max ${type} ID`);
        
        if (type === 'payment') {
          // For payments, use cursor-based pagination to get highest ID
          try {
            const response = await getAppliedPaymentCandidatesPaginated(10, null, 'next', '');
            if (response?.items && response.items.length > 0) {
              setCachedItems(response.items);
              const item = response.items[0]; // First item is highest ID (descending order)
              setSearchedData(transformPaymentData(item));
              setSearchId(''); // Clear input
              setCurrentIndex(1); // Start at index 1 for next click
              console.log(`Refresh found max payment (highest ID, cached ${response.items.length} items):`, item);
            } else {
              setSearchedData({ error: 'No payments found' });
            }
          } catch (error) {
            console.error('Failed to refresh payment:', error);
            setSearchedData({ error: 'Failed to refresh payment' });
          }
        } else {
          // Fetch fresh batch starting with max ID
          const endpoint = getApiEndpoint(type as 'customer' | 'invoice', 'list', undefined);
          const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}${endpoint}`, {
            params: { page: 1, pageSize: 10, search: '' } // Fetch 10 items for cycling
          });

          const dataKey = config.dataKey;
          if (response.data?.[dataKey] && response.data[dataKey].length > 0) {
            const items = response.data[dataKey];
            setCachedItems(items);
            const item = items[0]; // First item is max ID
            setSearchedData(item);
            setSearchId(''); // Clear input
            setCurrentIndex(1); // Start at index 1 for next click
            
            // QBO lookup will be triggered automatically by useEffect
            console.log(`Refresh found max ${type} (cached ${items.length} items):`, item);
          } else {
            setSearchedData({ error: `No ${type}s found` });
          }
        }
      }
    } catch (error) {
      console.error(`Failed to refresh ${type}:`, error);
      setSearchedData({ error: error.message });
    } finally {
      setLoading(false);
    }
  };

  // Simple button logic
  const hasInputValue = searchId.trim().length > 0;
  const hasCurrentData = searchedData && !searchedData.error;
  const hasCachedItems = cachedItems.length > 0;
  
  // Button text for lookup/refresh button
  const lookupRefreshText = hasInputValue ? 'Lookup' : 'Refresh';
  
  // Show back button if we have data (either from lookup or navigation) OR if we have cached items to go back to
  const showBackButton = hasCurrentData || (cachedItems.length > 0 && currentIndex > 1);

  // Determine status based on EServ and QBO data
  const getStatus = (): 'loading' | 'error' | 'eserv-only' | 'qbo-only' | 'both' | 'ready' => {
    if (loading) return 'loading';
    if (searchedData?.error) return 'error';
    if (searchedData && qboData) return 'both';
    if (searchedData && !qboData) return 'eserv-only';
    if (!searchedData && qboData) return 'qbo-only';
    return 'ready';
  };

  const getStatusText = (): string => {
    const status = getStatus();
    switch (status) {
      case 'loading': return 'Loading';
      case 'error': return 'Error';
      case 'both': return 'Found in both';
      case 'eserv-only': return 'Found in EServ only';
      case 'qbo-only': return 'Found in QBO only';
      case 'ready': return 'Ready';
      default: return 'Ready';
    }
  };

  const handleCrawl = () => {
    console.log(`🕷️ Starting crawl from current position...`);
    setCrawlStartIndex(currentIndex);
    setIsCrawling(true);
    // Clear previous crawl data when starting new crawl
    setCrawlData([]);
    // All crawls use "next" which decrements (lower IDs)
    // This ensures consistent behavior: crawl decrements until mismatch
    handleNext();
  };

  const handleStopCrawl = () => {
    console.log('🛑 Stopping crawl manually...');
    setIsCrawling(false);
  };

  const clearCrawlData = () => {
    setCrawlData([]);
  };

  // Auto-advance during crawl if everything is synced
  // Moved after handleNext declaration to avoid "used before declaration" error
  useEffect(() => {
    // Check if we've reached the end (no more items) - stop crawl for all types
    // This check runs even if loading/qboLoading to catch errors immediately
    if (isCrawling && searchedData?.error && !loading && !qboLoading) {
      const errorMessage = (searchedData.error as any)?.error || searchedData.error;
      const errorString = typeof errorMessage === 'string' ? errorMessage : String(errorMessage);
      
      if (errorString.toLowerCase().includes('no more') || errorString.toLowerCase().includes('reached end')) {
        console.log(`🛑 Crawl: Reached end of database (${errorString}), stopping crawl`);
        
        // Log end of crawl
        const currentId = (searchedData as any)?.id || (searchedData as any)?.payment_id || 'unknown';
        addLogEntry({
          action: 'crawl',
          type: type as 'customer' | 'invoice' | 'payment',
          id: String(currentId),
          success: false,
          message: `Crawl: Reached end of database`,
          error: errorString
        });
        
        // Add to crawl data
        setCrawlData(prev => [...prev, {
          timestamp: new Date().toISOString(),
          id: String(currentId),
          qboId: undefined,
          matches: 0,
          mismatches: 0,
          totalChecked: 0,
          status: 'error',
          details: `Reached end: ${errorString}`,
          type: type as 'customer' | 'invoice' | 'payment'
        }]);
        
        setIsCrawling(false);
        return;
      }
    }
    
    if (isCrawling && !loading && !qboLoading && searchedData && !searchedData.error && qboData) {
      if (type === 'customer') {
        const customerData = searchedData as CustomerData;
        
        // Check if customer is found in QBO
        const customerSynced = qboData?.found === true;
        
        // Check if all associated items are synced
        // If ignoreArchivedInvoices or ignoreUnsentInvoices is enabled, filter out those invoices from mismatch count
        const invoicesSyncStatus = customerData.invoices_sync_status || {};
        const invoicesArchiveStatus = customerData.invoices_archive_status || {};
        const invoicesSentStatus = customerData.invoices_sent_status || {};
        const ignoreArchived = crawlFilters?.ignoreArchivedInvoices || false;
        const ignoreUnsent = crawlFilters?.ignoreUnsentInvoices || false;
        
        // Count unsynced invoices, respecting filter settings
        // If ignoreArchived is false (not checked): archived unsynced invoices COUNT as mismatches → STOP crawl
        // If ignoreArchived is true (checked): archived unsynced invoices IGNORED → DON'T stop crawl
        // Same logic for ignoreUnsent
        const unsyncedInvoicesDetails = Object.entries(invoicesSyncStatus)
          .filter(([invoiceId, synced]) => synced === false)
          .map(([invoiceId, _]) => {
            const id = parseInt(invoiceId);
            return {
              id,
              isArchived: invoicesArchiveStatus[id] || false,
              isSent: invoicesSentStatus[id] || false,
              shouldIgnore: (ignoreArchived && invoicesArchiveStatus[id]) || (ignoreUnsent && !invoicesSentStatus[id])
            };
          });
        
        // Filter out invoices that should be ignored based on checkbox settings
        const unsyncedInvoices = unsyncedInvoicesDetails.filter(inv => !inv.shouldIgnore).length;
        const ignoredArchived = unsyncedInvoicesDetails.filter(inv => inv.isArchived && inv.shouldIgnore && ignoreArchived).length;
        const ignoredUnsent = unsyncedInvoicesDetails.filter(inv => !inv.isSent && inv.shouldIgnore && ignoreUnsent).length;
        
        // Count synced invoices (matches)
        const totalInvoices = Object.keys(invoicesSyncStatus).length;
        const syncedInvoices = Object.values(invoicesSyncStatus).filter(synced => synced === true).length;
        const totalUnsyncedInvoices = Object.values(invoicesSyncStatus).filter(synced => synced === false).length;
        
        // Count synced payments (matches)
        const paymentsSyncStatus = customerData.payments_sync_status || {};
        const totalPayments = Object.keys(paymentsSyncStatus).length;
        const syncedPayments = Object.values(paymentsSyncStatus).filter(synced => synced === true).length;
        const unsyncedPayments = Object.values(paymentsSyncStatus).filter(synced => synced === false).length;
        
        // Calculate totals
        const customerMatch = customerSynced ? 1 : 0;
        const customerMismatch = customerSynced ? 0 : 1;
        const totalMatches = customerMatch + syncedInvoices + syncedPayments;
        const totalMismatches = customerMismatch + unsyncedInvoices + unsyncedPayments;
        const totalChecked = 1 + totalInvoices + totalPayments; // customer + invoices + payments
        
        const allSynced = customerSynced && unsyncedInvoices === 0 && unsyncedPayments === 0;
        
        if (allSynced) {
          // Check if we've completed a full cycle
          const willLoop = currentIndex >= cachedItems.length;
          
          if (willLoop && currentIndex > crawlStartIndex + cachedItems.length) {
            console.log('🔄 Crawl: Completed full cycle, looping back to start...');
            console.log('   All customers in this batch are synced! Starting over from the beginning.');
          }
          
          console.log(`🕷️ Crawl: Customer fully synced, auto-decrementing... (${currentIndex}/${cachedItems.length})`);
          console.log(`   📊 Matches: ${totalMatches}/${totalChecked} | Mismatches: ${totalMismatches}/${totalChecked}`);
          console.log(`   ✅ Customer: ${customerMatch} match, ${customerMismatch} mismatch`);
          console.log(`   ✅ Invoices: ${syncedInvoices}/${totalInvoices} matches, ${unsyncedInvoices}/${totalInvoices} mismatches (not ignored)`);
          console.log(`   ✅ Payments: ${syncedPayments}/${totalPayments} matches, ${unsyncedPayments}/${totalPayments} mismatches`);
          
          const details = [
            `Customer: ${customerMatch} match, ${customerMismatch} mismatch`,
            `Invoices: ${syncedInvoices}/${totalInvoices} matches, ${unsyncedInvoices}/${totalInvoices} mismatches (not ignored)`,
            `Payments: ${syncedPayments}/${totalPayments} matches, ${unsyncedPayments}/${totalPayments} mismatches`
          ];
          if (ignoredArchived > 0 || ignoredUnsent > 0) {
            const ignored = [];
            if (ignoredArchived > 0) ignored.push(`${ignoredArchived} archived`);
            if (ignoredUnsent > 0) ignored.push(`${ignoredUnsent} unsent`);
            details.push(`Note: ${ignored.join(' and ')} unsynced invoice(s) ignored due to filter settings`);
            console.log(`   Note: ${ignored.join(' and ')} unsynced invoice(s) ignored due to filter settings`);
          }
          
          addLogEntry({
            action: 'crawl',
            type: 'customer',
            id: String(customerData.id || 'unknown'),
            success: true,
            qboId: customerData.customer_qbo_id || undefined,
            matches: totalMatches,
            mismatches: totalMismatches,
            totalChecked: totalChecked,
            message: `Crawl: Customer fully synced, continuing`,
            details: details.join('\n')
          });
          
          // Add to crawl data
          setCrawlData(prev => [...prev, {
            timestamp: new Date().toISOString(),
            id: String(customerData.id || 'unknown'),
            qboId: customerData.customer_qbo_id || undefined,
            matches: totalMatches,
            mismatches: totalMismatches,
            totalChecked: totalChecked,
            status: 'synced',
            details: details.join('\n'),
            type: 'customer'
          }]);
          
          setTimeout(() => handleNext(), 500); // Decrement using Next (next = decrement)
        } else {
          console.log('🛑 Crawl: Found sync issues, stopping crawl');
          console.log(`   📊 Matches: ${totalMatches}/${totalChecked} | Mismatches: ${totalMismatches}/${totalChecked}`);
          console.log(`   Customer synced: ${customerSynced} (${customerMatch} match, ${customerMismatch} mismatch)`);
          console.log(`   Unsynced invoices (not ignored): ${unsyncedInvoices}/${totalInvoices} (${syncedInvoices} matches, ${totalUnsyncedInvoices} total unsynced)`);
          
          const details = [
            `Customer: ${customerMatch} match, ${customerMismatch} mismatch`,
            `Unsynced invoices (not ignored): ${unsyncedInvoices}/${totalInvoices} (${syncedInvoices} matches, ${totalUnsyncedInvoices} total unsynced)`,
            `Unsynced payments: ${unsyncedPayments}/${totalPayments} (${syncedPayments} matches)`
          ];
          
          if (unsyncedInvoices > 0) {
            const mismatchDetails = unsyncedInvoicesDetails
              .filter(inv => !inv.shouldIgnore)
              .map(inv => `Invoice ${inv.id}${inv.isArchived ? ' (archived)' : ''}${inv.isSent ? ' (sent)' : ' (unsent)'}`)
              .slice(0, 5);
            details.push(`Mismatched invoices: ${mismatchDetails.join(', ')}`);
            console.log(`   Mismatched invoices: ${mismatchDetails.join(', ')}`);
          }
          console.log(`   Unsynced payments: ${unsyncedPayments}/${totalPayments} (${syncedPayments} matches)`);
          if (ignoredArchived > 0 || ignoredUnsent > 0) {
            const ignored = [];
            if (ignoredArchived > 0) ignored.push(`${ignoredArchived} archived`);
            if (ignoredUnsent > 0) ignored.push(`${ignoredUnsent} unsent`);
            details.push(`Note: ${ignored.join(' and ')} unsynced invoice(s) ignored due to filter settings (not stopping crawl)`);
            console.log(`   Note: ${ignored.join(' and ')} unsynced invoice(s) ignored due to filter settings (not stopping crawl)`);
          }
          
          addLogEntry({
            action: 'crawl',
            type: 'customer',
            id: String(customerData.id || 'unknown'),
            success: false,
            qboId: customerData.customer_qbo_id || undefined,
            matches: totalMatches,
            mismatches: totalMismatches,
            totalChecked: totalChecked,
            message: `Crawl: Found sync issues, stopping`,
            details: details.join('\n')
          });
          
          // Add to crawl data
          setCrawlData(prev => [...prev, {
            timestamp: new Date().toISOString(),
            id: String(customerData.id || 'unknown'),
            qboId: customerData.customer_qbo_id || undefined,
            matches: totalMatches,
            mismatches: totalMismatches,
            totalChecked: totalChecked,
            status: 'mismatch',
            details: details.join('\n'),
            type: 'customer'
          }]);
          
          setIsCrawling(false);
        }
      } else if (type === 'invoice') {
        const invoiceData = searchedData as InvoiceData;
        
        // Check if invoice is found in QBO
        const invoiceSynced = qboData?.found === true;
        
        // Check if customer is synced
        const customerQboId = invoiceData.customer_qbo_id;
        const customerSynced = customerQboId ? (invoiceData.customer_sync_status?.[customerQboId] === true) : undefined;
        
        // Calculate matches and mismatches
        const invoiceMatch = invoiceSynced ? 1 : 0;
        const invoiceMismatch = invoiceSynced ? 0 : 1;
        const customerMatch = customerSynced === undefined ? 0 : (customerSynced ? 1 : 0);
        const customerMismatch = customerSynced === undefined ? 0 : (customerSynced ? 0 : 1);
        const totalMatches = invoiceMatch + customerMatch;
        const totalMismatches = invoiceMismatch + customerMismatch;
        const totalChecked = customerQboId ? 2 : 1; // invoice + customer (if customer has QBO ID)
        
        // Invoice is fully synced if it exists in QBO and customer exists in QBO (if customer has QBO ID)
        const allSynced = invoiceSynced && (customerSynced === undefined || customerSynced === true);
        
        if (allSynced) {
          console.log(`🕷️ Crawl: Invoice fully synced, auto-decrementing... (${currentIndex}/${cachedItems.length})`);
          console.log(`   📊 Matches: ${totalMatches}/${totalChecked} | Mismatches: ${totalMismatches}/${totalChecked}`);
          console.log(`   ✅ Invoice: ${invoiceMatch} match, ${invoiceMismatch} mismatch`);
          if (customerQboId) {
            console.log(`   ✅ Customer: ${customerMatch} match, ${customerMismatch} mismatch`);
          }
          
          const details = [
            `Invoice: ${invoiceMatch} match, ${invoiceMismatch} mismatch`
          ];
          if (customerQboId) {
            details.push(`Customer: ${customerMatch} match, ${customerMismatch} mismatch`);
          }
          
          addLogEntry({
            action: 'crawl',
            type: 'invoice',
            id: String(invoiceData.id || 'unknown'),
            success: true,
            qboId: invoiceData.invoice_qbo_id || undefined,
            matches: totalMatches,
            mismatches: totalMismatches,
            totalChecked: totalChecked,
            message: `Crawl: Invoice fully synced, continuing`,
            details: details.join('\n')
          });
          
          // Add to crawl data
          setCrawlData(prev => [...prev, {
            timestamp: new Date().toISOString(),
            id: String(invoiceData.id || 'unknown'),
            qboId: invoiceData.invoice_qbo_id || undefined,
            matches: totalMatches,
            mismatches: totalMismatches,
            totalChecked: totalChecked,
            status: 'synced',
            details: details.join('\n'),
            type: 'invoice'
          }]);
          
          setTimeout(() => handleNext(), 500); // Decrement using Next (next = decrement)
        } else {
          console.log('🛑 Crawl: Found sync issues, stopping crawl');
          console.log(`   📊 Matches: ${totalMatches}/${totalChecked} | Mismatches: ${totalMismatches}/${totalChecked}`);
          console.log(`   Invoice synced: ${invoiceSynced} (${invoiceMatch} match, ${invoiceMismatch} mismatch)`);
          if (customerQboId) {
            console.log(`   Customer synced: ${customerSynced} (${customerMatch} match, ${customerMismatch} mismatch)`);
          }
          
          const details = [
            `Invoice: ${invoiceMatch} match, ${invoiceMismatch} mismatch`
          ];
          if (customerQboId) {
            details.push(`Customer: ${customerMatch} match, ${customerMismatch} mismatch`);
          }
          
          addLogEntry({
            action: 'crawl',
            type: 'invoice',
            id: String(invoiceData.id || 'unknown'),
            success: false,
            qboId: invoiceData.invoice_qbo_id || undefined,
            matches: totalMatches,
            mismatches: totalMismatches,
            totalChecked: totalChecked,
            message: `Crawl: Found sync issues, stopping`,
            details: details.join('\n')
          });
          
          // Add to crawl data
          setCrawlData(prev => [...prev, {
            timestamp: new Date().toISOString(),
            id: String(invoiceData.id || 'unknown'),
            qboId: invoiceData.invoice_qbo_id || undefined,
            matches: totalMatches,
            mismatches: totalMismatches,
            totalChecked: totalChecked,
            status: 'mismatch',
            details: details.join('\n'),
            type: 'invoice'
          }]);
          
          setIsCrawling(false);
        }
      } else if (type === 'payment') {
        // Check if payment is found in QBO
        const paymentSynced = qboData?.found === true;
        
        // Calculate matches and mismatches
        const paymentMatch = paymentSynced ? 1 : 0;
        const paymentMismatch = paymentSynced ? 0 : 1;
        const totalMatches = paymentMatch;
        const totalMismatches = paymentMismatch;
        const totalChecked = 1; // payment only
        
        if (paymentSynced) {
          console.log(`🕷️ Crawl: Payment fully synced, auto-decrementing... (${currentIndex}/${cachedItems.length})`);
          console.log(`   📊 Matches: ${totalMatches}/${totalChecked} | Mismatches: ${totalMismatches}/${totalChecked}`);
          console.log(`   ✅ Payment: ${paymentMatch} match, ${paymentMismatch} mismatch`);
          
          addLogEntry({
            action: 'crawl',
            type: 'payment',
            id: String((searchedData as PaymentData)?.payment_id || 'unknown'),
            success: true,
            qboId: (searchedData as PaymentData)?.payment_qbo_id?.toString() || undefined,
            matches: totalMatches,
            mismatches: totalMismatches,
            totalChecked: totalChecked,
            message: `Crawl: Payment fully synced, continuing`,
            details: `Payment: ${paymentMatch} match, ${paymentMismatch} mismatch`
          });
          
          // Add to crawl data
          setCrawlData(prev => [...prev, {
            timestamp: new Date().toISOString(),
            id: String((searchedData as PaymentData)?.payment_id || 'unknown'),
            qboId: (searchedData as PaymentData)?.payment_qbo_id?.toString() || undefined,
            matches: totalMatches,
            mismatches: totalMismatches,
            totalChecked: totalChecked,
            status: 'synced',
            details: `Payment: ${paymentMatch} match, ${paymentMismatch} mismatch`,
            type: 'payment'
          }]);
          
          setTimeout(() => handleNext(), 500); // Decrement using Next (next = decrement)
        } else {
          console.log('🛑 Crawl: Found sync issues, stopping crawl');
          console.log(`   📊 Matches: ${totalMatches}/${totalChecked} | Mismatches: ${totalMismatches}/${totalChecked}`);
          console.log(`   Payment synced: ${paymentSynced} (${paymentMatch} match, ${paymentMismatch} mismatch)`);
          
          addLogEntry({
            action: 'crawl',
            type: 'payment',
            id: String((searchedData as PaymentData)?.payment_id || 'unknown'),
            success: false,
            qboId: (searchedData as PaymentData)?.payment_qbo_id?.toString() || undefined,
            matches: totalMatches,
            mismatches: totalMismatches,
            totalChecked: totalChecked,
            message: `Crawl: Found sync issues, stopping`,
            details: `Payment: ${paymentMatch} match, ${paymentMismatch} mismatch`
          });
          
          // Add to crawl data
          setCrawlData(prev => [...prev, {
            timestamp: new Date().toISOString(),
            id: String((searchedData as PaymentData)?.payment_id || 'unknown'),
            qboId: (searchedData as PaymentData)?.payment_qbo_id?.toString() || undefined,
            matches: totalMatches,
            mismatches: totalMismatches,
            totalChecked: totalChecked,
            status: 'mismatch',
            details: `Payment: ${paymentMatch} match, ${paymentMismatch} mismatch`,
            type: 'payment'
          }]);
          
          setIsCrawling(false);
        }
      }
    }
  }, [isCrawling, loading, qboLoading, searchedData, qboData, crawlFilters, type, currentIndex, cachedItems.length, crawlStartIndex, handleNext]);

  return {
    loading,
    qboLoading,
    searchId,
    searchedData,
    qboData,
    setSearchId: handleSetSearchId,
    handleLookup,
    handleBack,
    handleRefresh,
    handleNext,
    handleLookupRefresh,
    handleCrawl,
    handleStopCrawl,
    isCrawling,
    config,
    lookupRefreshText,
    showBackButton,
    status: getStatus(),
    statusText: getStatusText(),
    crawlData,
    clearCrawlData
  };
};
